import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResourceWithPost } from '@app/src/api/fetchHooks'
import { CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS } from '@app/src/components/CreateNewRequestModal/Common/SelectFilterStep'
import useQueryFilters, { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import CategoryFilter from '@app/src/pages/ResourceCollection/Filters/CategoryFilter'
import Filter from '@app/src/pages/ResourceCollection/Filters/Filter'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { Category } from '@app/src/types/categories'
import { Grid } from '@mui/material'
import React, { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import useDeepCompareEffect from 'use-deep-compare-effect'

export const useCustomCategories = () => {
  const queryResult = useFetchResourceWithPost<Category[]>({
    key: FetchKey.Category,
    endpoint: endpoints.categoriesWithIncludes,
    body: ['options'],
  })
  const { data, ...result } = { ...queryResult, categories: queryResult.data }
  return result
}

interface CustomCategoriesFilterGridProps {
  formKey: string
  categories: Category[]
}

const CustomCategoriesFilterGrid: React.FC<CustomCategoriesFilterGridProps> = ({ formKey, categories }) => {
  useCategoryFilterSync(formKey, categories)

  return (
    <>
      {categories.map(category => (
        <Grid item xs={6} key={category.id}>
          <Filter uniqueId={category.id?.toString()} intersect operator={Operators.In} name="categoryOptions.id">
            {({ value, onChange }) => <CategoryFilter category={category} value={[value].flat()} onChange={onChange} />}
          </Filter>
        </Grid>
      ))}
    </>
  )
}

const findCategoryIdByOptionValue = (filterValue: string, categories: Category[]) =>
  categories.find(c => c.options?.some(op => op.id.toString() === filterValue))?.id

const mapFilterGroupToQueryParam = (filter: FilterGroup, categories: Category[]) => {
  const firstSection = filter?.filters?.[0]
  const filterSectionValue = firstSection?.value
  if (!filterSectionValue) return undefined

  const filterValues = Array.isArray(filterSectionValue)
    ? filterSectionValue.map(v => v.toString())
    : [filterSectionValue.toString()]

  const uniqueId = findCategoryIdByOptionValue(filterValues[0], categories)

  return {
    name: filter.name,
    value: filterValues,
    operator: [Operators.NotEqualTo, Operators.NotIn].includes(firstSection.operator) ? Operators.NotIn : Operators.In,
    ...(uniqueId ? { intersect: true, uniqueId } : {}),
  }
}

const useCategoryFilterSync = (formKey: string, categories: Category[] | undefined) => {
  const { setValue, getValues } = useFormContext()
  const initialFilterValues: FilterGroup[] | undefined = useMemo(() => getValues(formKey), [])

  const { setQueryFilters } = useQueryFilters(CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS)
  const filters = useGetApiQueryFilters(CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS)

  useDeepCompareEffect(() => setValue(formKey, filters), [filters])

  useDeepCompareEffect(() => {
    if (!initialFilterValues || !categories) return
    setQueryFilters(initialFilterValues.map(f => mapFilterGroupToQueryParam(f, categories)).filter(Boolean))
  }, [initialFilterValues, categories])
}

export default CustomCategoriesFilterGrid
